import { useSyncExternalStore } from 'react'
import { swapToUpdate } from './lib/updateGate'

/**
 * Tiny history router. Views are paths (`/`, `/map`, `/studio`); overlays on a view (sheets,
 * the phone's places list) are `#hash` entries of their own, so Back / Forward — and the
 * phone back gesture — step through the app instead of leaving it. Every entry we push
 * remembers the URL it was pushed from, so "back to the forecast" pops the stack instead of
 * piling up entries. `?mock=` survives navigation.
 */

export interface NavState {
  /** Depth of this entry within the visit (0 = the entry the visitor landed on). */
  depth: number
  /** URL of the entry this one was pushed from — only set on entries the app pushed. */
  back: string | null
  /** Overlay state of this entry (sheet / places list), restored on Back / Forward. */
  overlay: unknown
}

const KEY = 'wf'
const listeners = new Set<() => void>()

function emit() {
  for (const l of listeners) l()
}

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', emit)
  // `/index.html` is the home screen under its file name: show it as `/`.
  if (location.pathname === '/index.html') history.replaceState(history.state, '', `/${location.search}${location.hash}`)
}

export function navState(): NavState | null {
  if (typeof history === 'undefined') return null
  const s = history.state as Record<string, unknown> | null
  const v = s && typeof s === 'object' ? (s[KEY] as NavState | undefined) : undefined
  return v && typeof v.depth === 'number' ? v : null
}

const here = () => location.pathname + location.search + location.hash
const pathOf = (url: string) => new URL(url, location.origin).pathname

function write(kind: 'push' | 'replace', url: string, overlay: unknown) {
  const cur = navState()
  const next: NavState =
    kind === 'push'
      ? { depth: (cur?.depth ?? 0) + 1, back: here(), overlay }
      : { depth: cur?.depth ?? 0, back: cur?.back ?? null, overlay }
  if (kind === 'push') history.pushState({ [KEY]: next }, '', url)
  else history.replaceState({ ...((history.state as object | null) ?? {}), [KEY]: next }, '', url)
}

/** Marks the landing entry as ours (depth 0) without changing the URL. */
export function initNav(overlay: unknown = null): void {
  if (typeof history === 'undefined' || navState()) return
  history.replaceState({ ...((history.state as object | null) ?? {}), [KEY]: { depth: 0, back: null, overlay } }, '', here())
}

export function usePath(): string {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => location.pathname,
    () => '/',
  )
}

export type Route = 'home' | 'map' | 'studio' | 'notfound' | 'external'

/**
 * Which view a path is. `/`, `/map` and `/studio` (with or without a trailing slash) are the
 * app's; `/index.html` is the home screen; anything else is a dead link and gets the
 * not-found state rather than an empty sky. `/developers`, `/embed/*`, `/v2/*` and `/api/*` are
 * the host's own pages and functions: the app only ever sees them when a stale service worker
 * answered with the app shell, and then steps aside (see ExternalPage in App.tsx).
 */
export function routeOf(path: string): Route {
  const at = (base: string) => path === base || path.startsWith(`${base}/`)
  if (['/developers', '/embed', '/v2', '/api'].some(at)) return 'external'
  if (at('/map')) return 'map'
  if (at('/studio')) return 'studio'
  if (path === '/' || path === '/index.html' || path === '') return 'home'
  return 'notfound'
}

function withMock(path: string): string {
  const mock = new URLSearchParams(location.search).get('mock')
  return mock != null ? `${path}${path.includes('?') ? '&' : '?'}mock=${encodeURIComponent(mock)}` : path
}

/** A focus target to restore when the view we are leaving comes back (see useReturnFocus). */
let returnKey: string | null = null

/**
 * Go to a view. Heading back to the view we came from is a real history Back (the map's and
 * the Studio's back buttons don't grow the history), anything else is a new entry.
 */
export function navigate(path: string, replace = false): void {
  if (path === location.pathname && !location.hash) return
  const active = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null
  const key = active?.closest?.('[data-nav-key]')?.getAttribute('data-nav-key')
  if (key) returnKey = key
  const cur = navState()
  if (!replace && cur?.back != null && pathOf(cur.back) === path) {
    history.back()
    return
  }
  // A deploy happened while this tab was open: the new view is a real page load of the new build.
  if (!replace && swapToUpdate(withMock(path))) return
  write(replace ? 'replace' : 'push', withMock(path), null)
  emit()
}

/** The data-nav-key of the control that left this view, consumed once when it's back. */
export function takeReturnKey(): string | null {
  const k = returnKey
  returnKey = null
  return k
}

export function hrefFor(path: string): string {
  const mock = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('mock') : null
  return mock != null ? `${path}${path.includes('?') ? '&' : '?'}mock=${encodeURIComponent(mock)}` : path
}

// ─── Overlays (sheets, the places list) ─────────────────────────────────────

const base = () => location.pathname + location.search

/** Opens an overlay as its own history entry (`#hash`). */
export function pushOverlay(hash: string, overlay: unknown): void {
  write('push', `${base()}#${hash}`, overlay)
}

/** Updates the current entry's overlay in place (a tab or day changed inside a sheet). */
export function replaceOverlay(hash: string | null, overlay: unknown): void {
  write('replace', hash ? `${base()}#${hash}` : base(), overlay)
}

/**
 * Leaves the current overlay entry: a real Back when the app pushed it on this view (the
 * popstate handler then restores the entry below), otherwise just drops the hash.
 * Returns true when it went Back (the caller waits for popstate).
 */
export function popOverlay(): boolean {
  const cur = navState()
  if (cur?.back != null && cur.overlay != null && pathOf(cur.back) === location.pathname) {
    history.back()
    return true
  }
  replaceOverlay(null, null)
  return false
}

/** Keeps the address bar's search params in step (e.g. the selected place) without a new entry. */
export function replaceSearch(params: URLSearchParams): void {
  const q = params.toString()
  const url = `${location.pathname}${q ? `?${q}` : ''}${location.hash}`
  if (url === here()) return
  history.replaceState(history.state, '', url)
}
